import { useState, useEffect, useCallback, useMemo } from "react";
import { useAdminApi } from "../adminUtils";
import { useStudio } from "../../../studios/StudioContext";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-misc.css";
import "../admin-responsive.css";
import "./newslettersTab.css";
import "./socialTab.css";

// Social posts → Facebook / Instagram / Bluesky (server: /api/social, socialFormat/socialPublish/socialCard).
// Posts go out from the active studio's accounts. Flow: write → Preview → Publish.

const PLATFORMS = [
  { id: "facebook",  label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "bluesky",   label: "Bluesky" },
];

const SWATCHES = [
  { key: "background", label: "Background" },
  { key: "text",       label: "Title" },
  { key: "accent",     label: "Accent" },
  { key: "accent2",    label: "Label" },
];

const EMPTY = {
  body: "", linkUrl: "", hashtags: "",
  photos: [],                       // [{ url, alt, width, height, mimeType }]
  useCard: true, kicker: "", cardTitle: "", cardLines: "", cardPhoto: "",
  palette: {},                      // per-post colour overrides
  platforms: ["facebook", "instagram", "bluesky"],
};

// Shrink before upload: max 1600px on the long side, JPEG (Instagram's API only takes JPEG)
function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale  = Math.min(1, 1600 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => b
        ? resolve({ file: new File([b], "photo.jpg", { type: "image/jpeg" }), width: canvas.width, height: canvas.height })
        : reject(new Error("Could not read image")), "image/jpeg", 0.88);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

function payloadText(platform, payload) {
  if (!payload) return "";
  if (platform === "facebook")  return payload.params?.caption ?? payload.params?.message ?? payload.message ?? "";
  if (platform === "instagram") return payload.caption ?? "";
  if (platform === "bluesky")   return payload.record?.text ?? "";
  return "";
}

function countFor(platform, text) {
  if (platform === "bluesky") {
    const n = typeof Intl !== "undefined" && Intl.Segmenter
      ? [...new Intl.Segmenter().segment(text)].length : [...text].length;
    return `${n} / 300`;
  }
  if (platform === "instagram") return `${text.length} / 2,200`;
  return `${text.length} characters`;
}

export default function SocialTab() {
  const { API, AUTH } = useAdminApi();
  const { studioId, studioName } = useStudio();

  const [status, setStatus]       = useState(null);   // { facebook, instagram, bluesky } for this studio
  const [basePalette, setBase]    = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [preview, setPreview]     = useState(null);   // { images, facebook, instagram, bluesky }
  const [previewKey, setPrevKey]  = useState("");
  const [results, setResults]     = useState(null);
  const [busy, setBusy]           = useState(null);
  const [error, setError]         = useState(null);

  // ── Load connection status + studio palette ─────────────
  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        fetch(`${API}/social/status`, { headers: AUTH }).then(r => r.json()),
        fetch(`${API}/social/palettes`, { headers: AUTH }).then(r => r.json()),
      ]);
      if (s.error) { setError(s.error); return; }
      setStatus(s[studioId] ?? s);
      setBase(p[studioId] ?? null);
    } catch { setError("Couldn't reach the server."); }
  }, [API, AUTH, studioId]);

  useEffect(() => { load(); }, [load]);

  const connected = id => !!status?.[id];
  const palette = useMemo(() => ({ ...(basePalette || {}), ...form.palette }), [basePalette, form.palette]);

  const edit = patch => { setForm(f => ({ ...f, ...patch })); setResults(null); setError(null); };

  const togglePlatform = id => edit({
    platforms: form.platforms.includes(id) ? form.platforms.filter(p => p !== id) : [...form.platforms, id],
  });

  // ── Photos (same upload endpoint as Newsletters) ────────
  const upload = async file => {
    const { file: jpg, width, height } = await resizeImage(file);
    const fd = new FormData();
    fd.append("image", jpg);
    const r = await fetch(`${API}/newsletter/images`, { method: "POST", headers: { Authorization: AUTH.Authorization }, body: fd });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Upload failed.");
    return { url: d.url, width, height, mimeType: "image/jpeg", alt: file.name.replace(/\.[^.]+$/, "") };
  };

  const addPhotos = async e => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy("upload"); setError(null);
    try {
      const added = [];
      for (const f of files) added.push(await upload(f));
      edit({ photos: [...form.photos, ...added] });
    } catch (ex) { setError(ex.message); }
    finally { setBusy(null); }
  };

  const setCardPhoto = async e => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("upload"); setError(null);
    try { edit({ cardPhoto: (await upload(f)).url }); }
    catch (ex) { setError(ex.message); }
    finally { setBusy(null); }
  };

  const setPhoto = (i, patch) => edit({ photos: form.photos.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const removePhoto = i => edit({ photos: form.photos.filter((_, j) => j !== i) });

  // ── Build the request body ─────────────────────────────
  const buildPost = () => {
    const post = {
      body: form.body,
      hashtags: form.hashtags.split(/[\s,]+/).filter(Boolean),
      images: form.photos,
    };
    if (form.linkUrl.trim()) post.link = { url: /^https?:\/\//.test(form.linkUrl.trim()) ? form.linkUrl.trim() : `https://${form.linkUrl.trim()}` };
    if (form.useCard && form.cardTitle.trim()) {
      post.card = {
        kicker: form.kicker.trim(),
        title: form.cardTitle.trim(),
        lines: form.cardLines.split("\n").map(s => s.trim()).filter(Boolean),
        ...(form.cardPhoto ? { photo: form.cardPhoto } : {}),
        ...(Object.keys(form.palette).length ? { palette: form.palette } : {}),
      };
    }
    return post;
  };

  const formKey = JSON.stringify(form);
  const previewFresh = preview && previewKey === formKey;
  const chosen = form.platforms.filter(connected);

  const handlePreview = async () => {
    setError(null); setResults(null);
    if (!form.body.trim() && !form.cardTitle.trim() && !form.photos.length) { setError("Write something or add a photo first."); return; }
    setBusy("preview");
    try {
      const r = await fetch(`${API}/social/preview`, {
        method: "POST", headers: AUTH,
        body: JSON.stringify({ studio: studioId, post: buildPost(), platforms: form.platforms.length ? form.platforms : PLATFORMS.map(p => p.id) }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Preview failed."); return; }
      setPreview(d);
      setPrevKey(formKey);
    } catch { setError("Something went wrong."); }
    finally { setBusy(null); }
  };

  const handlePublish = async () => {
    if (!previewFresh || !chosen.length) return;
    const names = PLATFORMS.filter(p => chosen.includes(p.id)).map(p => p.label).join(", ");
    if (!window.confirm(`Post to ${names} as ${studioName}? This goes out publicly right away.`)) return;
    setBusy("publish"); setError(null);
    try {
      // Re-use the previewed images (incl. the generated card) so what's posted is what was shown
      const post = { ...buildPost(), images: preview.images || form.photos };
      delete post.card;
      const r = await fetch(`${API}/social/publish`, {
        method: "POST", headers: AUTH,
        body: JSON.stringify({ studio: studioId, post, platforms: chosen }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Publish failed."); return; }
      setResults(d);
    } catch { setError("Something went wrong."); }
    finally { setBusy(null); }
  };

  const startOver = () => {
    if (!window.confirm("Clear this post and start a new one?")) return;
    setForm(EMPTY); setPreview(null); setPrevKey(""); setResults(null); setError(null);
  };

  // ═══ Render ════════════════════════════════════════════
  return (
    <div className="adm-tab-content nl-tab sc-tab">
      <div className="nl-head">
        <h2 className="nl-title">Social posts</h2>
        <span className="sc-as">Posting as {studioName}</span>
        <button className="adm-btn-ghost-sm" onClick={startOver}>New post</button>
      </div>

      <div className="sc-accounts">
        {PLATFORMS.map(p => (
          <span key={p.id} className={`sc-account ${connected(p.id) ? "on" : "off"}`}>
            {p.label}: {status == null ? "…" : connected(p.id) ? (typeof status[p.id] === "string" ? `@${status[p.id]}` : "connected") : "not connected"}
          </span>
        ))}
      </div>

      <div className="nl-editor">
        {/* ── Left: compose ── */}
        <div className="nl-col-edit">
          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Post</h3>
            <label className="adm-form-label">Text
              <textarea className="adm-input sc-body" rows={6} value={form.body}
                placeholder="What's happening at the studio? Links in the text work on Facebook and Bluesky."
                onChange={e => edit({ body: e.target.value })} />
            </label>
            <label className="adm-form-label">Booking link (optional)
              <input className="adm-input" value={form.linkUrl} placeholder="https://…  (a class, workshop or pass link)"
                onChange={e => edit({ linkUrl: e.target.value })} />
            </label>
            <label className="adm-form-label">Hashtags (optional)
              <input className="adm-input" value={form.hashtags} placeholder="yoga stpete iyengaryoga"
                onChange={e => edit({ hashtags: e.target.value })} />
            </label>
          </div>

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Image card</h3>
            <label className="nl-check">
              <input type="checkbox" checked={form.useCard} onChange={e => edit({ useCard: e.target.checked })} />
              Make a branded image in {studioName}'s colours
            </label>
            {form.useCard && (
              <>
                <div className="nl-row">
                  <label className="adm-form-label sc-kicker">Label
                    <input className="adm-input" value={form.kicker} placeholder="Workshop"
                      onChange={e => edit({ kicker: e.target.value })} />
                  </label>
                  <label className="adm-form-label">Title
                    <input className="adm-input" value={form.cardTitle} placeholder="Backbends & Shoulder Opening"
                      onChange={e => edit({ cardTitle: e.target.value })} />
                  </label>
                </div>
                <label className="adm-form-label">Details (one per line, up to 3)
                  <textarea className="adm-input" rows={3} value={form.cardLines}
                    placeholder={"Sat, Nov 15 · 10:00 am – 12:00 pm\nwith Dominique"}
                    onChange={e => edit({ cardLines: e.target.value })} />
                </label>
                <div className="nl-row sc-card-photo">
                  {form.cardPhoto && <img src={form.cardPhoto} alt="" />}
                  <label className="adm-btn-ghost-sm sc-file">
                    {busy === "upload" ? "Uploading…" : form.cardPhoto ? "Replace card photo" : "Add a photo to the card"}
                    <input type="file" accept="image/jpeg,image/png" hidden onChange={setCardPhoto} disabled={!!busy} />
                  </label>
                  {form.cardPhoto && <button type="button" className="adm-btn-ghost-sm" onClick={() => edit({ cardPhoto: "" })}>Remove</button>}
                </div>

                <div className="sc-swatches">
                  {SWATCHES.map(s => (
                    <label key={s.key} className="adm-form-label nl-color">{s.label}
                      <input type="color" value={palette[s.key] || "#000000"}
                        onChange={e => edit({ palette: { ...form.palette, [s.key]: e.target.value } })} />
                    </label>
                  ))}
                  {Object.keys(form.palette).length > 0 && (
                    <button type="button" className="adm-btn-ghost-sm" onClick={() => edit({ palette: {} })}>Reset to studio colours</button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Other photos</h3>
            {form.photos.map((p, i) => (
              <div key={p.url} className="sc-photo">
                <img src={p.url} alt="" />
                <label className="adm-form-label">Description (for screen readers)
                  <input className="adm-input" value={p.alt} onChange={e => setPhoto(i, { alt: e.target.value })} />
                </label>
                <button type="button" className="nl-icon nl-icon-del" title="Remove" onClick={() => removePhoto(i)}>✕</button>
              </div>
            ))}
            <label className="adm-btn-ghost-sm sc-file">
              {busy === "upload" ? "Uploading…" : "+ Add photos"}
              <input type="file" accept="image/jpeg,image/png" multiple hidden onChange={addPhotos} disabled={!!busy} />
            </label>
            <p className="adm-hint">Instagram needs at least one image: the card or a photo. Bluesky shows up to 4.</p>
          </div>

          <div className="adm-settings-section nl-actions">
            <div className="sc-platforms">
              {PLATFORMS.map(p => (
                <label key={p.id} className={`nl-check ${connected(p.id) ? "" : "sc-disabled"}`}>
                  <input type="checkbox" disabled={!connected(p.id)}
                    checked={connected(p.id) && form.platforms.includes(p.id)}
                    onChange={() => togglePlatform(p.id)} />
                  {p.label}{!connected(p.id) && " (not connected)"}
                </label>
              ))}
            </div>
            {error && <p className="adm-error">{error}</p>}
            <div className="nl-row">
              <button className="adm-btn-ghost-sm" onClick={handlePreview} disabled={!!busy}>
                {busy === "preview" ? "Making preview…" : previewFresh ? "Preview is up to date" : "Preview"}
              </button>
            </div>
            <button className="adm-btn-primary nl-send" onClick={handlePublish}
              disabled={!!busy || !previewFresh || !chosen.length}>
              {busy === "publish" ? "Posting…"
                : !chosen.length ? "No connected platforms selected"
                : !previewFresh ? "Preview before posting"
                : `Post to ${PLATFORMS.filter(p => chosen.includes(p.id)).map(p => p.label).join(", ")}`}
            </button>

            {results && (
              <div className="sc-results">
                {Object.entries(results).map(([p, r]) => (
                  <p key={p} className={r.ok ? "nl-notice" : "adm-error"}>
                    {r.ok ? "✓ " : "✗ "}{PLATFORMS.find(x => x.id === p)?.label}:{" "}
                    {r.ok ? (r.url ? <a href={r.url} target="_blank" rel="noreferrer">view post</a> : "posted") : r.error}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: preview ── */}
        <div className="nl-col-preview sc-preview">
          <div className="nl-preview-bar">
            <span className="nl-block-label">Preview</span>
            {preview && !previewFresh && <span className="sc-stale">Changed since preview</span>}
          </div>
          {!preview && <p className="adm-empty">Press Preview to see the image and how the post reads on each platform.</p>}
          {preview && (
            <>
              {preview.images?.length > 0 && (
                <div className="sc-preview-images">
                  {preview.images.map(img => <img key={img.url} src={img.url} alt={img.alt || ""} />)}
                </div>
              )}
              {PLATFORMS.filter(p => preview[p.id]).map(p => {
                const r = preview[p.id];
                const text = payloadText(p.id, r.payload);
                return (
                  <div key={p.id} className="nl-block sc-platform">
                    <div className="nl-block-head">
                      <span className="nl-block-label">{p.label}</span>
                      <span className="sc-count">{r.ok ? countFor(p.id, text) : "won't post"}</span>
                    </div>
                    <div className="nl-block-body">
                      {r.ok && <pre className="sc-text">{text || "(no text)"}</pre>}
                      {r.errors?.map(e => <p key={e} className="adm-error">{e}</p>)}
                      {r.warnings?.map(w => <p key={w} className="adm-hint">{w}</p>)}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
